import type { QuizAssignedDataWithAttempts, QuizDetailsForTeacherData } from '@repo/api/quiz/quizService';
import type { QuizPrivate, QuizPublic } from '@repo/types/quiz';
import type { ComponentProps } from 'react';

import { cn } from '@repo/lib/cn';
import { format, secondsToMinutes } from 'date-fns';
import { AwardIcon, CalendarIcon, CheckCheckIcon, CheckIcon, TimerIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { useCredentials } from '@/store/auth-store';

interface QuizBasicStatsProps extends ComponentProps<'div'> {
  quiz: QuizAssignedDataWithAttempts | QuizDetailsForTeacherData | QuizPrivate | QuizPublic;
}

export default function QuizBasicStats(quizBasicStatsProps: QuizBasicStatsProps) {
  const { lvl } = useCredentials();
  const { quiz, className, ...props } = quizBasicStatsProps;

  return (
    <div className={cn('grid grid-cols-2 gap-3', className)} {...props}>
      <div className="flex flex-col items-center justify-center gap-1">
        <span className="text-muted-foreground text-xs font-medium">Czas wykonania</span>
        <Badge variant="muted" size="sm">
          <TimerIcon />
          {quiz.time ? `${secondsToMinutes(quiz.time)} min` : 'Nieograniczony'}
        </Badge>
      </div>
      <div className="flex flex-col items-center justify-center gap-1">
        <span className="text-muted-foreground text-xs font-medium">Termin wykonania</span>
        <Badge variant="muted" size="sm">
          <CalendarIcon />
          {quiz.deadline ? format(new Date(quiz.deadline), 'dd.MM.yyyy HH:mm') : 'Brak'}
        </Badge>
      </div>
      <div className="flex flex-col items-center justify-center gap-1">
        <span className="text-muted-foreground text-xs font-medium">Max ilość punktów</span>
        <Badge variant="muted" size="sm">
          <AwardIcon />
          max {quiz.points}
        </Badge>
      </div>
      <div className="flex flex-col items-center justify-center gap-1">
        <span className="text-muted-foreground text-xs font-medium">{lvl > 0 ? 'Max ilość podejść' : 'Ilość podejść'}</span>
        <QuizAttemptBadge attempts={'quizresps' in quiz && Array.isArray(quiz.quizresps) ? quiz.quizresps.length : 0} maxAttempts={quiz.maxAttempts} />
      </div>
    </div>
  );
}

interface QuizAttemptBadgeProps {
  attempts: number;
  maxAttempts: number;
}

function QuizAttemptBadge(quizAttemptBadgeProps: QuizAttemptBadgeProps) {
  const { attempts, maxAttempts } = quizAttemptBadgeProps;
  const { lvl } = useCredentials();

  const isUnlimited: boolean = maxAttempts === 0;
  const notStarted: boolean = attempts === 0;
  const maxAttemptsReached: boolean = attempts >= maxAttempts;

  if (isUnlimited) {
    return (
      <Badge variant="muted" size="sm">
        Nieograniczona
      </Badge>
    );
  }
  if (lvl > 0) {
    return (
      <Badge variant="muted" size="sm">
        <CheckCheckIcon /> max {maxAttempts}
      </Badge>
    );
  }
  if (notStarted) {
    return (
      <Badge variant="warning" size="sm">
        {attempts} / {maxAttempts}
      </Badge>
    );
  }
  if (maxAttemptsReached) {
    return (
      <Badge variant="success" size="sm">
        {' '}
        <CheckIcon />
        {attempts} / {maxAttempts}
      </Badge>
    );
  }

  return (
    <Badge variant="blue" size="sm">
      <CheckCheckIcon /> {attempts} / {maxAttempts}
    </Badge>
  );
}
